import { FormInstance } from 'antd';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';

import { EmployeeSalaryTypesEnum } from '@thinknet-gmbh/platform-common';
import {
  CheckboxSelect,
  Form,
  Loader,
  NumberInput,
  SelectInput,
  TextAreaInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';

import { useRouteQuery } from 'src/common/helpers';
import { useGetAllPlatformQuestionnaireSetting } from 'src/hooks/employee-questionnaire-settings/get-all-employee-questionnaire-settings.hook';
import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';

import JobLocationsList from './components/job-locations-list.component';

import { IJob } from 'src/pages/jobs/jobs.interfaces';
import { MainContainer } from './job-info-form-items.styles';
import React from 'react';

const JobInfoFormItems = ({
  initialValues,
  form,
}: {
  initialValues?: IJob;
  form: FormInstance<any>;
}) => {
  const { t } = useTranslation(['common', 'jobs', 'settings', 'products']);
  const query = useRouteQuery();
  const type: string = query.get('type') || '';

  const { companies, loading } = useGetAllPlatformCompanies();
  const { questionnaireSetting = [] } = useGetAllPlatformQuestionnaireSetting();

  if (loading) {
    return <Loader />;
  }

  return (
    <MainContainer>
      <div>
        <Form.Item
          name="title"
          label={t('common:labels.jobTitle')}
          rules={[{ required: true, message: t('common:validation.error.jobTitleRequired') }]}
        >
          {type ? (
            <SelectInput placeholder="Choose needed job title">
              <SelectInput.Option value="job">Job title</SelectInput.Option>
            </SelectInput>
          ) : (
            <TextInput
              maxLength={30}
              placeholder={t('settings:companySettings.labels.hrManager')}
            />
          )}
        </Form.Item>
        <Form.Item
          name="activityKey"
          label={t('jobs:labels.activityKey')}
          rules={[
            {
              pattern: /^\d{5}$/,
              message: t('jobs:validation.mustBeExactDigits', {
                value: '5',
              }),
            },
          ]}
        >
          <NumberInput
            allowNegative={false}
            thousandSeparator={false}
            allowLeadingZeros={false}
            placeholder="222"
            maxLength={5}
          />
        </Form.Item>
        <Form.Item name="description" label={t('products:planList.createPlan.labels.description')}>
          <TextAreaInput
            maxLength={1000}
            showCount
            placeholder={t('products:planList.createPlan.placeholders.writeDescription')}
          />
        </Form.Item>

        <Form.Item
          name="companyId"
          label={t('common:labels.company')}
          rules={[{ required: true, message: t('common:validation.error.companyRequired') }]}
        >
          <SelectInput placeholder={t('settings:company.placeholders.chooseCompany')}>
            {companies.map((company: any) => (
              <SelectInput.Option key={company._id} value={company._id}>
                {company.name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="questionnaireSettingsIds"
          label={t('employee:questionnaire.labels.questionnaireSettings')}
        >
          <CheckboxSelect
            placeholder="Select from the list"
            optionData={questionnaireSetting?.map((item: { name: string; _id: string }) => ({
              label: item?.name,
              value: item?._id,
            }))}
          />
        </Form.Item>
      </div>
      <JobLocationsList initialValues={initialValues} form={form} />
      <div>
        <Form.Item
          name="workingHours"
          label={t('employee:labels.workingHours')}
          rules={[{ required: true, message: t('jobs:validation.workingHoursRequired') }]}
        >
          <NumberInput
            inputMode="numeric"
            suffix={` ${t('common:labels.times.hours')}`}
            maxLength={10}
            placeholder={`40 ${t('common:labels.times.hours')}`}
          />
        </Form.Item>
        <Form.Item
          name="salaryType"
          label={t('employee:labels.salaryType')}
          rules={[{ required: true, message: t('employee:validationError.salaryTypeRequired') }]}
        >
          <SelectInput placeholder={t(`employee:workingConditions.labels.salaryWages`)}>
            {Object.values(EmployeeSalaryTypesEnum).map(item => (
              <SelectInput.Option key={item} value={item}>
                {t(`employee:workingConditions.labels.${camelCase(item.toLowerCase())}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="vacation"
          label={t('jobs:labels.vacation')}
          rules={[{ required: true, message: t('jobs:validation.vacationRequired') }]}
        >
          <NumberFormat
            customInput={TextInput}
            suffix={` ${t('common:labels.times.days')}`}
            maxLength={10}
            placeholder={`80 ${t('common:labels.times.days')}`}
          />
        </Form.Item>
        <Form.Item
          name="appraisalReview"
          label={t('jobs:labels.appraisalReview')}
          rules={[{ required: true, message: t('jobs:validation.appraisalReviewRequired') }]}
        >
          <NumberFormat
            customInput={TextInput}
            suffix={` ${t('jobs:labels.perYear')}`}
            placeholder={t('jobs:placeholders.appraisalReview')}
            maxLength={15}
          />
        </Form.Item>
      </div>
    </MainContainer>
  );
};

export default JobInfoFormItems;
